import { useState, useEffect, useRef } from "react";
import "../../../../styles/screens/client-visit-details/manage-users/AddNewUserForm.css";
import Required from "../../../common-components/Required";
import CloseButton from "../../../custom-components/CloseButton";
import CancelButton from "../../../custom-components/CancelButton";
import BlackButton from "../../../custom-components/BlackButton";
import { UserScreenStringLabels } from "../../../../util/Strings";
import { NewUserFormValidations } from "../../../../util/Validations";
import ErrorMessageComponent from "../../../common-components/ErrorMessageComponent";

import countryData from "./../../../../dummyData/CountryCodes.json";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  getAllAttendeesOfVisit,
  checkUserEmailInAnotherVisit,
  resetEmailCheckData,
} from "../../../../slices/SpeakerSlice";

// Import the email validator
import { isValidEmail } from "../../../../util/Utils";

const getInitialState = (data) => ({
  name: data?.name || "",
  email: data?.email || "",
  phoneNumber: data?.phoneNumber || "",
  countryData: data?.countryData || countryData[0]?.code || "",
  attendeeId: data?.attendeeId || "",
});

function AddNewUserForm({ show, onClickNext, data }) {
  const [state, setState] = useState(getInitialState(data));
  const [validation, setValidation] = useState({});
  const [emailValid, setEmailValid] = useState(true);

  const dispatch = useDispatch();
  const visitData = useSelector((state) => state.visits);
  const users = useSelector((state) => state.users.userData);
  const id = useSelector((state) => state.visits.selectedVisitId);

  const userOptions = useSelector((state) =>
    state.speakers.visitAttendeeOptions.filter(
      (item1) => !users.some((item) => item?.attendeeId === item1?.value)
    )
  );

  const emailCheckData = useSelector((state) => state.speakers.emailCheckData);
  const emailCheckLoading = useSelector(
    (state) => state.speakers.emailCheckLoading
  );

  const debounceTimeoutRef = useRef(null);

  // Reset and re-initialize form when it opens or data changes
  useEffect(() => {
    if (show) {
      setState(getInitialState(data));
      dispatch(resetEmailCheckData());
      setEmailValid(true);
      setValidation({});
    }
  }, [show, data, dispatch]);

  // Fetch attendees on internal visits mount/change
  useEffect(() => {
    if (visitData?.isInternalVisit && id) {
      dispatch(getAllAttendeesOfVisit(id));
    }
  }, [dispatch, id, visitData?.isInternalVisit]);

  // Clear debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  // Helper to reset all form state
  const resetForm = () => {
    setState(getInitialState(null));
    setValidation({});
    setEmailValid(true);
    dispatch(resetEmailCheckData());
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  // Handle input change, including email check debounce
  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === "email") value = value.toLowerCase();

    setState((prev) => ({ ...prev, [e.target.name]: value }));

    if (e.target.name === "email") {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

      const trimmedEmail = value.trim().toLowerCase();

      // Reset emailCheckData and errors on each new email change
      dispatch(resetEmailCheckData());
      setEmailValid(true);
      setValidation((prev) => ({ ...prev, email: "" }));

      if (!trimmedEmail) {
        setEmailValid(true);
        setValidation((prev) => ({ ...prev, email: "" }));
        return;
      }

      if (!isValidEmail(trimmedEmail)) {
        setEmailValid(false);
        setValidation((prev) => ({ ...prev, email: "Invalid email format" }));
        return;
      }

      // Valid email - debounce API call
      setEmailValid(true);
      setValidation((prev) => ({ ...prev, email: "" }));

      debounceTimeoutRef.current = setTimeout(() => {
        dispatch(checkUserEmailInAnotherVisit({ email: trimmedEmail }));
      }, 500);
    }
  };

  const checkValidation = () => {
    const { errors, isValid } = NewUserFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  // Form completion check to disable submit
  const isFormComplete = () =>
    state.name.trim() !== "" &&
    state.email.trim() !== "" &&
    state.phoneNumber.trim() !== "" &&
    state.countryData.trim() !== "" &&
    emailValid &&
    !emailCheckLoading;

  const handleNextButton = () => {
    if (!checkValidation()) return;

    const newData = {
      ...state,
      ...(visitData.isInternalVisit ? { attendeeId: state.attendeeId } : {}),
    };

    if (emailCheckData?._id) {
      newData.existingVisitId = emailCheckData._id;
    }

    onClickNext(newData);

    // Reset everything after successful submit
    resetForm();
  };

  const handleCancel = () => {
    // Always reset form on cancel regardless of edit/add mode
    resetForm();
    onClickNext();
  };

  if (!show) return null;

  const formComplete = isFormComplete();

  return (
    <div className="modal">
      <div className="form-scroll">
        <div className="form-flex">
          <div
            className="user-form-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="form-header">
              <div className="header">
                {data
                  ? UserScreenStringLabels.userFormUpdate
                  : UserScreenStringLabels.userFormAdd}
              </div>
              <CloseButton onClickhandle={handleCancel} />
            </div>

            <div className="form-container">
              {/* Name */}
              <div className="form-label-input">
                <label className="form-label">
                  {UserScreenStringLabels.name}
                  <Required />
                </label>
                {!visitData.isInternalVisit ? (
                  <div className="form-input-container">
                    <input
                      type="text"
                      name="name"
                      value={state.name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Input"
                      required
                    />
                  </div>
                ) : (
                  <Select
                    options={userOptions}
                    className="basic-multi-select"
                    isMulti={false}
                    classNamePrefix="select"
                    name="name"
                    closeMenuOnSelect
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        name: e.label,
                        attendeeId: e.value,
                      }))
                    }
                    value={
                      state.attendeeId
                        ? userOptions.find((u) => u.value === state.attendeeId)
                        : null
                    }
                  />
                )}
                <ErrorMessageComponent message={validation.name} />
              </div>

              {/* Email */}
              <div className="form-label-input">
                <label className="form-label">
                  {UserScreenStringLabels.email}
                  <Required />
                </label>
                <div
                  className="form-input-container"
                  style={{ position: "relative" }}
                >
                  <input
                    type="email"
                    name="email"
                    value={state.email}
                    onChange={handleChange}
                    placeholder="Input"
                    required
                    className="form-input"
                    autoComplete="off"
                  />
                  {emailCheckLoading && (
                    <div className="email-check-loading">
                      <span>{UserScreenStringLabels.checkingEmailMessage}</span>
                      <span className="spinner" />
                    </div>
                  )}
                  {emailCheckData?._id && (
                    <div className="email-exists-message">
                      {UserScreenStringLabels.userExistMessage}{" "}
                      <strong>{emailCheckData.name || "another visit"}</strong>
                    </div>
                  )}
                </div>
                <ErrorMessageComponent message={validation.email} />
              </div>

              {/* Country Code */}
              <div className="form-label-input">
                <label className="form-label">
                  {UserScreenStringLabels.countryCode}
                </label>
                <select
                  value={state.countryData}
                  name="countryData"
                  onChange={handleChange}
                >
                  {countryData.map(({ code, name, dial_code }) => (
                    <option key={code} value={code}>
                      {name} {dial_code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone */}
              <div className="form-label-input">
                <label className="form-label">
                  {UserScreenStringLabels.phone}
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={state.phoneNumber}
                    name="phoneNumber"
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Input"
                  />
                </div>
              </div>

              {/* Footer buttons */}
              <div className="footer-button-container">
                <CancelButton onClickhandle={handleCancel}>
                  {UserScreenStringLabels.cancel}
                </CancelButton>
                <BlackButton
                  onClickhandle={handleNextButton}
                  disabled={!formComplete}
                  style={{
                    backgroundColor: !formComplete ? "lightgrey" : undefined,
                    cursor: !formComplete ? "not-allowed" : "pointer",
                  }}
                >
                  {emailCheckData?._id
                    ? "Delete and Add"
                    : data
                    ? UserScreenStringLabels.update
                    : UserScreenStringLabels.add}
                </BlackButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNewUserForm;